import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import OutilSinistre from './OutilSinistre';
import { theme, pageTitleStyle, pageTitleTextStyle, secondaryButtonStyle } from '../../theme';

const BASE = '/api';
function authFetch(url, opts={}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers||{}), ...(token?{Authorization:`Bearer ${token}`}:{}) } });
}

const STATUTS = {
  ouverture_pendante: { bg:'#FEF3C7', color:'#92400E', dot:'#F59E0B' },
  ouverte:            { bg:'#FEE2E2', color:'#991B1B', dot:'#EF4444' },
  cloturee:           { bg:'#F1F5F9', color:'#475569', dot:'#94A3B8' },
};
const ORANGE = theme.orange;
const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);

function fmtDate(d)   { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }
function fmtDateTime(d) { return d ? new Date(d).toLocaleString('fr-FR') : '—'; }

const inp = { border:'1px solid #D9D9D5',borderRadius:8,padding:'10px 12px',fontSize:14,
  boxSizing:'border-box',width:'100%',fontFamily:'inherit',outline:'none' };
const lbl = { fontSize:12,fontWeight:500,color:'#64748B',display:'block',marginBottom:6 };

export default function FicheSinistre() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isManager } = useAuth();

  const [sinistre, setSinistre]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [showCloturer, setShowCloturer] = useState(false);

  // Edit state
  const [dateOuv, setDateOuv]   = useState('');
  const [dateFin, setDateFin]   = useState('');
  const [notes, setNotes]       = useState('');

  useEffect(() => {
    authFetch(`${BASE}/sinistres/${id}`)
      .then(r => r.json())
      .then(d => {
        setSinistre(d);
        setDateOuv(d.date_ouverture?.slice(0,10) || '');
        setDateFin(d.date_fin_indemnisations?.slice(0,10) || '');
        setNotes(d.notes || '');
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  async function save() {
    setSaving(true); setError('');
    try {
      const res  = await authFetch(`${BASE}/sinistres/${id}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ date_ouverture:dateOuv, date_fin_indemnisations:dateFin||null, notes }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setSinistre(data); setEditing(false);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function cloturer() {
    setSaving(true); setError('');
    try {
      const res  = await authFetch(`${BASE}/sinistres/${id}/cloturer`, { method:'POST' });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setSinistre(prev => ({ ...prev, statut:'cloturee', date_cloture:data.date_cloture }));
      setShowCloturer(false);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{padding:24,color:'#94A3B8'}}>{t('sinistres.loading')}</div>;
  if (!sinistre) return <div style={{padding:24,color:'#DC2626'}}>{t('common.not_found') || 'Procédure introuvable.'}</div>;

  const st = STATUTS[sinistre.statut] || STATUTS.cloturee;
  const isClosed = sinistre.statut === 'cloturee';
  const isPending = sinistre.statut === 'ouverture_pendante';

  return (
    <div>
      {/* ── En-tête : titre souligné (style "Ligne") ──────────── */}
      <div style={{ ...pageTitleStyle, marginBottom:20, alignItems:'center' }}>
        <button onClick={() => navigate('/sinistres')}
          style={{ background:theme.surfaceAlt, border:`0.5px solid ${theme.border}`, width:34, height:34, borderRadius:8,
            cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <SvgIcon size={18} color={theme.textSecondary}><path d="M15 6l-6 6l6 6"/></SvgIcon>
        </button>
        <div style={{ width:42, height:42, borderRadius:'50%', background:theme.sidebarBg,
          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <SvgIcon size={22} color={ORANGE}><path d="M12 9v4M10.36 3.59l-8.1 13.53a1.9 1.9 0 0 0 1.63 2.88h16.22a1.9 1.9 0 0 0 1.63 -2.87l-8.1 -13.54a1.9 1.9 0 0 0 -3.27 0zM12 16h.01"/></SvgIcon>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, color:theme.textTertiary, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:2 }}>
            {t('sinistres.procedure_label')}
          </div>
          <div style={{ ...pageTitleTextStyle, fontFamily:'monospace' }}>{sinistre.numero_procedure}</div>
        </div>
        <span style={{ display:'inline-flex',alignItems:'center',gap:6,background:st.bg,
          color:st.color,borderRadius:99,padding:'5px 14px',fontSize:12,fontWeight:500 }}>
          <span style={{ width:7,height:7,borderRadius:'50%',background:st.dot }}/>
          {{'ouverture_pendante':t('sinistres.statut_ouverture_pendante'),'ouverte':t('sinistres.statut_ouverte'),'cloturee':t('sinistres.statut_cloturee')}[sinistre.statut] || sinistre.statut}
        </span>
        {!isClosed && !isPending && isManager && (
          <button onClick={() => setShowCloturer(true)}
            style={{ ...secondaryButtonStyle, padding:'8px 15px' }}>
            {t('sinistres.cloturer')}
          </button>
        )}
      </div>

      {error && (
        <div style={{ background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:7,
          padding:'9px 14px',marginBottom:14,fontSize:13,color:'#991B1B' }}>⚠ {error}</div>
      )}

      {/* ── Alerte : en attente de confirmation ───────────────── */}
      {isPending && (
        <div style={{ background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:8,
          padding:'12px 16px',marginBottom:16,display:'flex',gap:10,alignItems:'center' }}>
          <span style={{ fontSize:20 }}>⏳</span>
          <div>
            <div style={{ fontWeight:700,color:'#92400E',fontSize:13 }}>{t('sinistres.pending_alert')}</div>
            <div style={{ fontSize:12,color:'#92400E' }}>{t('sinistres.pending_desc')}</div>
          </div>
        </div>
      )}

      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16 }}>

        {/* ── Informations de la procédure ──────────────────────── */}
        <div style={{ background:'#fff',borderRadius:10,border:'0.5px solid #E6E9ED',padding:20 }}>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16 }}>
            <div style={{ fontSize:14,fontWeight:600,display:'flex',alignItems:'center',gap:8 }}>
              <SvgIcon size={18} color={ORANGE}><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/></SvgIcon>
              {t('sinistres.infos_procedure')}
            </div>
            {!isClosed && !editing && (
              <button onClick={() => setEditing(true)}
                style={{ fontSize:13,padding:'6px 13px',borderRadius:8,border:'1px solid #D9D9D5',
                  cursor:'pointer',background:'#fff',color:'#475569',display:'inline-flex',alignItems:'center',gap:6 }}>
                <SvgIcon size={15}><path d="M9 7h-3a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-3M9 15h3l8.5 -8.5a1.5 1.5 0 0 0 -3 -3l-8.5 8.5v3M16 5l3 3"/></SvgIcon>
                {t('common.edit') === 'common.edit' ? t('common.modifier') : t('common.edit')}
              </button>
            )}
          </div>

          {/* Agence (toujours verrouillée si client_locked) */}
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>{t('sinistres.col_agence')}</label>
            <div style={{ display:'flex',alignItems:'center',gap:9,background:'#F7F7F5',
              borderRadius:8,padding:'10px 12px',fontSize:14,color:'#1A1A1A' }}>
              <SvgIcon size={17} color={ORANGE}><path d="M3 21l18 0M5 21v-16a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v16M9 7h2M9 11h2M9 15h2"/></SvgIcon>
              <strong>{sinistre.client_nom}</strong>
              {sinistre.client_locked && (
                <span style={{ fontSize:11,color:'#64748B',marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:4 }}>
                  <SvgIcon size={13} color="#94A3B8"><path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2zM11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0M8 11v-4a4 4 0 1 1 8 0v4"/></SvgIcon>
                  {t('sinistres.agence_locked')}
                </span>
              )}
            </div>
          </div>

          {editing ? (
            <>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12 }}>
                <div>
                  <label style={lbl}>{t('sinistres.date_ouverture').replace(' *','')}</label>
                  <input type="date" value={dateOuv} onChange={e=>setDateOuv(e.target.value)} style={inp}/>
                </div>
                <div>
                  <label style={lbl}>{t('sinistres.date_fin')}</label>
                  <input type="date" value={dateFin} onChange={e=>setDateFin(e.target.value)} min={dateOuv} style={inp}/>
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={lbl}>{t('sinistres.notes')}</label>
                <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3}
                  style={{ ...inp, resize:'vertical' }}/>
              </div>
              <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
                <button onClick={() => { setEditing(false); setDateOuv(sinistre.date_ouverture?.slice(0,10)||''); setDateFin(sinistre.date_fin_indemnisations?.slice(0,10)||''); setNotes(sinistre.notes||''); }}
                  style={{ fontSize:13,padding:'6px 14px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff' }}>
                  Annuler
                </button>
                <button onClick={save} disabled={saving}
                  style={{ fontSize:13,padding:'8px 16px',borderRadius:8,background:ORANGE,
                    color:'#fff',border:'none',cursor:'pointer',fontWeight:500,opacity:saving?.5:1 }}>
                  {saving ? '…' : t('common.save')}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:11,color:'#64748B',fontWeight:600,marginBottom:2 }}>{t('sinistres.date_ouverture').replace(' *','').toUpperCase()}</div>
                  <div style={{ fontSize:14,fontWeight:700,color:'#1A1A1A' }}>{fmtDate(sinistre.date_ouverture)}</div>
                </div>
                <div>
                  <div style={{ fontSize:11,color:'#64748B',fontWeight:600,marginBottom:2 }}>{t('sinistres.date_fin').toUpperCase()}</div>
                  <div style={{ fontSize:14,fontWeight:700,color:'#1A1A1A' }}>{fmtDate(sinistre.date_fin_indemnisations)}</div>
                </div>
              </div>
              {sinistre.notes && (
                <div style={{ background:'#F8FAFC',borderRadius:7,padding:'9px 12px',fontSize:13,color:'#475569' }}>
                  {sinistre.notes}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Traçabilité ────────────────────────────────────────── */}
        <div style={{ background:'#fff',borderRadius:10,border:'0.5px solid #E6E9ED',padding:20 }}>
          <div style={{ fontSize:14,fontWeight:600,marginBottom:16,display:'flex',alignItems:'center',gap:8 }}>
            <SvgIcon size={18} color={ORANGE}><path d="M12 8l0 4l2 2M12 3a9 9 0 1 0 9 9a9 9 0 0 0 -9 -9"/></SvgIcon>
            {t('sinistres.tracabilite')}
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            <div style={{ display:'flex',gap:10,alignItems:'flex-start' }}>
              <div style={{ width:8,height:8,borderRadius:'50%',background:ORANGE,marginTop:4,flexShrink:0 }}/>
              <div>
                <div style={{ fontSize:12,fontWeight:700 }}>{t('sinistres.cree')}</div>
                <div style={{ fontSize:12,color:'#64748B' }}>{fmtDateTime(sinistre.created_at)}</div>
                {sinistre.cree_par_nom && <div style={{ fontSize:11,color:'#94A3B8' }}>{t('sinistres.par')} {sinistre.cree_par_nom}</div>}
              </div>
            </div>
            {sinistre.date_confirmation && (
              <div style={{ display:'flex',gap:10,alignItems:'flex-start' }}>
                <div style={{ width:8,height:8,borderRadius:'50%',background:'#DC2626',marginTop:4,flexShrink:0 }}/>
                <div>
                  <div style={{ fontSize:12,fontWeight:700,color:'#DC2626' }}>{t('sinistres.confirmee')}</div>
                  <div style={{ fontSize:12,color:'#64748B' }}>{fmtDateTime(sinistre.date_confirmation)}</div>
                  {sinistre.confirme_par_nom && <div style={{ fontSize:11,color:'#94A3B8' }}>{t('sinistres.par')} {sinistre.confirme_par_nom}</div>}
                </div>
              </div>
            )}
            {sinistre.date_cloture && (
              <div style={{ display:'flex',gap:10,alignItems:'flex-start' }}>
                <div style={{ width:8,height:8,borderRadius:'50%',background:'#94A3B8',marginTop:4,flexShrink:0 }}/>
                <div>
                  <div style={{ fontSize:12,fontWeight:700,color:'#475569' }}>{t('sinistres.cloturee_label')}</div>
                  <div style={{ fontSize:12,color:'#64748B' }}>{fmtDateTime(sinistre.date_cloture)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <OutilSinistre procedureId={id} procedureStatut={sinistre?.statut}/>

      {/* ── Dialog : Clôturer ─────────────────────────────────── */}
      {showCloturer && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'#fff',borderRadius:12,padding:24,width:420,maxWidth:'90vw' }}>
            <div style={{ fontSize:16,fontWeight:800,marginBottom:8 }}>{t('sinistres.cloturer_confirm_title')}</div>
            <p style={{ fontSize:13,color:'#64748B',marginBottom:20 }}>
              {t('sinistres.cloturer_confirm_body')}
            </p>
            <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
              <button onClick={()=>setShowCloturer(false)}
                style={{ fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff' }}>
                Annuler
              </button>
              <button onClick={cloturer} disabled={saving}
                style={{ fontSize:14,padding:'7px 16px',borderRadius:6,background:'#4B5563',
                  color:'#fff',border:'none',cursor:'pointer',fontWeight:700,opacity:saving?.5:1 }}>
                {saving ? '…' : t('sinistres.cloturer_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
